import {
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
} from 'react';
import Popover from './Popover';
import Listbox, { type ListboxOptionState } from './Listbox';
import { useListNavigation } from '../../hooks/useListNavigation';
import { type AnchoredWidth } from '../../hooks/useAnchoredPosition';
import { cx, getOptionId, useUiText } from './primitiveUtils';

/**
 * An editable combobox: a text field with a suggestion list that opens BELOW it.
 * See Apps/web/THEME.md.
 *
 * ARIA follows the APG "list autocomplete" pattern, as DistrictCombobox in
 * components/market-ui/PlaceField.tsx does: the input keeps focus and carries
 * `role="combobox"`, `aria-expanded`, `aria-controls`, `aria-autocomplete="list"` and
 * `aria-activedescendant`.
 *
 * The caller owns the text and the option list (it filters, fetches, debounces);
 * this component owns opening, the active option and the keyboard.
 *
 * Keys: ArrowDown/ArrowUp open and move, Enter picks the active option (or, with
 * `allowFreeText`, submits the typed text when nothing is active), Escape closes,
 * Tab closes and moves on. Home/End stay with the text caret.
 */

type NativeInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  | 'value'
  | 'defaultValue'
  | 'onChange'
  | 'id'
  | 'role'
  | 'disabled'
  | 'placeholder'
  | 'type'
  | 'className'
  | 'aria-expanded'
  | 'aria-controls'
  | 'aria-autocomplete'
  | 'aria-activedescendant'
>;

export interface ComboboxProps<T> {
  /** Input id (generated when omitted). The listbox id is `${id}-listbox`. */
  id?: string;
  /** Accessible name; visible above the field unless `hideLabel` (default for 'bare'). */
  label: string;
  hideLabel?: boolean;
  inputValue: string;
  onInputChange: (value: string) => void;
  options: readonly T[];
  getOptionKey: (option: T) => string;
  renderOption: (option: T, state: ListboxOptionState) => ReactNode;
  onSelect: (option: T) => void;
  /** Key of the chosen option, marked `aria-selected` and highlighted on open. */
  selectedKey?: string | null;
  loading?: boolean;
  /** Shown when typed text matches nothing. Null hides the row. Default: «لا توجد نتائج مطابقة». */
  emptyText?: string | null;
  /** Enter with no active option submits the typed text through `onSubmitFreeText`. */
  allowFreeText?: boolean;
  onSubmitFreeText?: (text: string) => void;
  placeholder?: string;
  /** Icon at the inline start (sized to 1.25rem). */
  leadingIcon?: ReactNode;
  /** 'field': bordered form field. 'bare': borderless, fills a cell of a larger bar. */
  variant?: 'field' | 'bare';
  /**
   * 'bare' only: show the label inside the cell, small, above the value (the home
   * hero's tall fields, after wasalt.sa). The label stays the input's <label>.
   */
  inlineLabel?: boolean;
  /** Open the list when the field gains focus (e.g. to show featured cities). */
  openOnFocus?: boolean;
  disabled?: boolean;
  className?: string;

  /** Called by the clear button, after the text is emptied (reset a chosen value here). */
  onClear?: () => void;
  /** Highlight the first match while typing, so Enter picks it. Default false. */
  autoActivateFirst?: boolean;
  isOptionDisabled?: (option: T) => boolean;
  invalid?: boolean;
  popoverWidth?: AnchoredWidth;
  popoverMinWidth?: number | string;
  /** Listbox accessible name. Default: `label`. */
  listLabel?: string;
  inputRef?: Ref<HTMLInputElement | null>;
  /** Extra attributes for the <input> (inputMode, name, aria-describedby, onBlur...). */
  inputProps?: NativeInputProps;
}

export default function Combobox<T>({
  id: idProp,
  label,
  hideLabel,
  inputValue,
  onInputChange,
  options,
  getOptionKey,
  renderOption,
  onSelect,
  selectedKey = null,
  loading = false,
  emptyText,
  allowFreeText = false,
  onSubmitFreeText,
  placeholder,
  leadingIcon,
  variant = 'field',
  inlineLabel = false,
  openOnFocus = false,
  disabled = false,
  className = '',
  onClear,
  autoActivateFirst = false,
  isOptionDisabled,
  invalid = false,
  popoverWidth = 'anchor',
  popoverMinWidth,
  listLabel,
  inputRef: inputRefProp,
  inputProps,
}: ComboboxProps<T>) {
  const tx = useUiText();
  const generatedId = useId();
  const inputId = idProp ?? `combobox-${generatedId}`;
  const listId = `${inputId}-listbox`;
  const labelId = `${inputId}-label`;

  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(inputRefProp, () => inputRef.current, []);

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const isDisabledAt = useCallback(
    (i: number) => !!isOptionDisabled && i >= 0 && i < options.length && isOptionDisabled(options[i]),
    [isOptionDisabled, options],
  );

  const choose = useCallback(
    (option: T) => {
      onSelect(option);
      setOpen(false);
      setActiveIndex(-1);
    },
    [onSelect],
  );

  const nav = useListNavigation({
    count: options.length,
    activeIndex,
    onActiveIndexChange: setActiveIndex,
    homeEnd: false,
    isDisabled: isDisabledAt,
    onSelect: (i) => choose(options[i]),
  });

  const selectedIndex = useMemo(
    () => (selectedKey == null ? -1 : options.findIndex((o) => getOptionKey(o) === selectedKey)),
    [getOptionKey, options, selectedKey],
  );

  const resolvedEmpty = emptyText === undefined ? tx('combobox.noResults', 'No matching results') : emptyText;
  const hasQuery = inputValue.trim().length > 0;
  const showEmpty = !loading && options.length === 0 && hasQuery && resolvedEmpty !== null;
  const listVisible = open && !disabled && (options.length > 0 || loading || showEmpty);

  const { firstEnabled, lastEnabled } = nav;
  const firstEnabledRef = useRef(firstEnabled);
  firstEnabledRef.current = firstEnabled;
  const optionCount = options.length;

  // Keep the active option inside the list as the caller re-filters it.
  useEffect(() => {
    setActiveIndex((i) => (i >= optionCount ? -1 : i));
  }, [optionCount]);

  // `autoActivateFirst`: after the user types, highlight the first match once the
  // caller's filtered list for that text has rendered. Keyed on text and count, not on
  // the options array, so an unmemoised list cannot reset arrow-key movement.
  const autoPickRef = useRef(false);
  useEffect(() => {
    if (!autoPickRef.current) return;
    autoPickRef.current = false;
    setActiveIndex(firstEnabledRef.current());
  }, [inputValue, optionCount]);

  const openList = (initial: 'selected' | 'first' | 'last' | 'none') => {
    if (disabled) return;
    setOpen(true);
    if (initial === 'none') return;
    if (initial === 'selected' && selectedIndex >= 0) setActiveIndex(selectedIndex);
    else if (initial === 'last') setActiveIndex(lastEnabled());
    else if (initial === 'first') setActiveIndex(firstEnabled());
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    inputProps?.onKeyDown?.(event);
    if (event.defaultPrevented || event.nativeEvent.isComposing) return;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        if (!listVisible) {
          event.preventDefault();
          if (event.altKey) openList('none');
          else openList(selectedIndex >= 0 ? 'selected' : event.key === 'ArrowDown' ? 'first' : 'last');
          return;
        }
        if (event.altKey && event.key === 'ArrowUp') {
          event.preventDefault();
          setOpen(false);
          return;
        }
        nav.handleKeyDown(event);
        return;
      case 'PageDown':
      case 'PageUp':
        if (listVisible) nav.handleKeyDown(event);
        return;
      case 'Enter':
        if (listVisible && activeIndex >= 0 && nav.handleKeyDown(event)) return;
        if (allowFreeText && onSubmitFreeText) {
          event.preventDefault();
          setOpen(false);
          setActiveIndex(-1);
          onSubmitFreeText(inputValue);
          return;
        }
        // With the list open, Enter must not fall through to an implicit form submit.
        if (listVisible) event.preventDefault();
        return;
      case 'Escape':
        // While the list shows, the popover consumes Escape first; this covers the
        // open-but-empty state.
        if (open) {
          event.preventDefault();
          setOpen(false);
          setActiveIndex(-1);
        }
        return;
      case 'Tab':
        setOpen(false);
        return;
      default:
    }
  };

  const clear = () => {
    onInputChange('');
    onClear?.();
    setActiveIndex(-1);
    setOpen(openOnFocus);
    inputRef.current?.focus();
  };

  const showClear = !disabled && !loading && (inputValue.length > 0 || (!!onClear && selectedKey != null));
  const labelInside = inlineLabel && variant === 'bare';
  const labelHidden = labelInside ? false : (hideLabel ?? variant === 'bare');
  const statusText = loading
    ? tx('combobox.loading', 'Loading…')
    : showEmpty && open
      ? (resolvedEmpty ?? '')
      : '';

  const fieldClasses =
    variant === 'field'
      ? cx(
          'min-h-[2.75rem] rounded-xl border bg-[var(--pv-color-surface-strong)] transition-colors duration-200',
          'dark:bg-[#08372F]',
          invalid
            ? 'border-[#DF2F2B] focus:border-[#DF2F2B] focus:ring-2 focus:ring-[#DF2F2B]/20'
            : 'border-[var(--pv-border-soft)] hover:border-[var(--pv-color-accent-soft)] focus:border-[#004D40] focus:ring-2 focus:ring-[#004D40]/20 dark:focus:border-[#3FBBA2] dark:focus:ring-[#3FBBA2]/25',
          'disabled:cursor-not-allowed disabled:bg-[var(--pv-color-surface-soft)]',
        )
      : 'h-full min-h-[2.75rem] border-0 bg-transparent focus:ring-0';

  return (
    <div className={cx('relative min-w-0', variant === 'bare' && 'h-full', className)}>
      <label
        id={labelId}
        htmlFor={inputId}
        className={
          labelInside
            ? cx(
                'pointer-events-none absolute top-[7px] z-[1] truncate text-[0.75rem] font-semibold leading-none text-[var(--pv-color-text)]',
                leadingIcon ? 'start-11 end-4' : 'start-4 end-4',
              )
            : labelHidden
              ? 'sr-only'
              : 'mb-1.5 block text-[0.875rem] font-medium text-[var(--pv-color-text)]'
        }
      >
        {label}
      </label>
      <div
        ref={anchorRef}
        className={cx(
          'relative',
          variant === 'bare' &&
            'h-full rounded-[inherit] focus-within:ring-2 focus-within:ring-inset focus-within:ring-[#004D40]/20 dark:focus-within:ring-[#3FBBA2]/25',
        )}
      >
        {leadingIcon && (
          <span
            className={cx(
              'pointer-events-none absolute inset-y-0 start-0 flex w-11 items-center justify-center text-[1.5rem] leading-none text-[var(--pv-color-primary)] [&_svg]:h-6 [&_svg]:w-6',
              labelInside && 'pt-[17px]',
            )}
            aria-hidden="true"
          >
            {leadingIcon}
          </span>
        )}
        <input
          {...inputProps}
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          aria-expanded={listVisible}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={listVisible && activeIndex >= 0 ? getOptionId(listId, activeIndex) : undefined}
          aria-invalid={invalid || inputProps?.['aria-invalid'] || undefined}
          autoComplete={inputProps?.autoComplete ?? 'off'}
          disabled={disabled}
          placeholder={placeholder}
          value={inputValue}
          onChange={(event) => {
            const next = event.target.value;
            autoPickRef.current = autoActivateFirst && next.trim().length > 0;
            onInputChange(next);
            setActiveIndex(-1);
            setOpen(true);
          }}
          onFocus={(event) => {
            inputProps?.onFocus?.(event);
            if (openOnFocus) openList('selected');
          }}
          onClick={(event) => {
            inputProps?.onClick?.(event);
            if (!open && (openOnFocus || inputValue.trim())) openList('selected');
          }}
          onKeyDown={onKeyDown}
          className={cx(
            'w-full min-w-0 text-base text-[var(--pv-color-text)] outline-none',
            'placeholder:text-[var(--pv-color-text-muted)] dark:text-[#E6F3EF]',
            leadingIcon ? 'ps-11' : 'ps-4',
            'pe-11',
            labelInside && 'pt-[17px]',
            fieldClasses,
          )}
        />
        <span className={cx('absolute inset-y-0 end-0 flex w-11 items-center justify-center', labelInside && 'pt-[17px]')}>
          {loading && (
            <i
              className="ri-loader-4-line animate-spin text-[1.125rem] leading-none text-[var(--pv-color-primary)]"
              aria-hidden="true"
            />
          )}
          {showClear && (
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={clear}
              aria-label={tx('combobox.clear', 'Clear')}
              className="grid h-full min-h-0 w-full place-items-center rounded-full text-[var(--pv-color-text-muted)] transition-colors hover:text-[var(--pv-color-primary)] focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-[var(--pv-color-primary)]"
            >
              <i className="ri-close-line text-[1.125rem] leading-none" aria-hidden="true" />
            </button>
          )}
        </span>
      </div>

      {/* Announced politely: loading and "no results". Kept outside the popover so it
          exists before its text changes, which is what makes screen readers speak it. */}
      <span role="status" className="sr-only">
        {statusText}
      </span>

      <Popover
        open={listVisible}
        onOpenChange={(next) => {
          if (!next) {
            setOpen(false);
            setActiveIndex(-1);
          }
        }}
        anchorRef={anchorRef}
        width={popoverWidth}
        minWidth={popoverMinWidth}
      >
        <Listbox
          id={listId}
          options={options}
          getOptionKey={(o) => getOptionKey(o)}
          renderOption={renderOption}
          activeIndex={activeIndex}
          onActiveIndexChange={setActiveIndex}
          onSelect={(o) => choose(o)}
          isSelected={selectedKey == null ? undefined : (o) => getOptionKey(o) === selectedKey}
          isDisabled={isOptionDisabled ? (o) => isOptionDisabled(o) : undefined}
          aria-label={listLabel ?? label}
          busy={loading}
        />
        {options.length === 0 && (loading || showEmpty) && (
          <div
            className="flex min-h-[2.75rem] items-center gap-2 px-3.5 py-2.5 text-[0.9375rem] text-[var(--pv-color-text-muted)]"
            aria-hidden="true"
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line animate-spin text-[1.125rem] leading-none" />
                {tx('combobox.loading', 'Loading…')}
              </>
            ) : (
              resolvedEmpty
            )}
          </div>
        )}
      </Popover>
    </div>
  );
}
